import React, { ChangeEvent, useState } from 'react';
import { useT } from 'client/i18n/hooks/useT';
import { ButtonPrimary, Type } from '@spotify-internal/encore-web';
import { getIsValidUrl } from 'modules/Url';
import { FieldInput, LoadingButton } from '@spotify-internal/anchor-ds';
import { useUpdateDistribution } from 'client/hooks/distribution/useUpdateDistribution';
import { RedirectConfirmationModal } from 'client/screens/SettingsScreen/components/RedirectPodcast/RedirectConfirmationModal';
import {
  RedirectPodcastParagraph,
  FieldGroup,
  RedirectPodcastContainer,
  RedirectSection,
} from './styles';
import { trackRedirectEvents } from '../../events';

type RedirectPodcastProps = {
  podcastRedirectDestination?: string;
  isPrePublish: boolean;
};

export function RedirectPodcast({
  isPrePublish,
  podcastRedirectDestination,
}: RedirectPodcastProps) {
  const t = useT();
  const [url, setUrl] = useState(podcastRedirectDestination || '');
  const [isRedirected, setIsRedirected] = useState(false);
  const [isUrlValid, setIsUrlValid] = useState(true);
  const [
    shouldShowConfirmationModal,
    setShouldShowConfirmationModal,
  ] = useState(false);

  const { updateDistribution, status } = useUpdateDistribution();

  // Update distribution with podcastRedirectDestination
  // as current value of url
  const handleSubmit = async () => {
    try {
      await updateDistribution(
        { podcastRedirectDestination: url },
        {
          onSuccess: () => {
            setShouldShowConfirmationModal(false);
            setIsRedirected(true);
          },
        }
      );
    } catch (e) {
      throw new Error(`Couldn't add url; error: ${e}`);
    }
  };

  // Update distribution with podcastRedirectDestination
  // as null to remove url
  const handleRemoveButtonClicked = async () => {
    try {
      await updateDistribution(
        { podcastRedirectDestination: null },
        {
          onSuccess: () => {
            setIsRedirected(false);
            setUrl('');
          },
        }
      );
    } catch (e) {
      throw new Error(`Couldn't remove url; error: ${e}`);
    }
  };

  const handleRedirectButtonClicked = () => {
    trackRedirectEvents();
    if (getIsValidUrl(url)) {
      setShouldShowConfirmationModal(true);
      setIsUrlValid(true);
    } else {
      setIsUrlValid(false);
    }
  };

  const hideConfirmationModal = () => {
    setShouldShowConfirmationModal(false);
  };

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setUrl(e.target.value);
  };

  return (
    <RedirectSection>
      <Type as="h3" variant="celloCanon">
        {t(
          'SettingsScreen.RedirectYourPodcast.RedirectYourPodcastHeader',
          'Redirect your podcast',
          'Redirect your podcast section header'
        )}
      </Type>
      <RedirectPodcastContainer>
        <RedirectPodcastParagraph>
          {t(
            'SettingsScreen.RSSDistribution.RedirectUrl.RedirectPodcastExplanation',
            'You can switch your podcast to a different hosting service by redirecting from Anchor to the URL of your new host. Paste the URL of your podcast at the new host below. (ex.  https://www.newhost.com/podcastname)',

            'redirect podcast explanation'
          )}
        </RedirectPodcastParagraph>
        <FieldGroup
          label={t(
            'SettingsScreen.RSSDistribution.RedirectUrl.RedirectURLFieldLabel',
            'Redirect URL',
            'Redirect URL field label'
          )}
          labelFor="url"
        >
          <FieldInput
            name="url"
            type="url"
            defaultValue={url}
            placeholder={t(
              'SettingsScreen.RSSDistribution.RedirectUrl.inputPlaceholderText',
              'New host URL',
              'Redirect URL placeholder text'
            )}
            onChange={handleOnChange}
            disabled={isRedirected || isPrePublish}
            error={
              !isUrlValid
                ? {
                    message: t(
                      'PodcastAvailabilityScreen.RSSDistribution.RedirectUrl.RedirectURLFieldError',
                      'Invalid URL',
                      'Redirect URL field error'
                    ),
                  }
                : undefined
            }
          />
        </FieldGroup>
        {isRedirected ? (
          <LoadingButton
            colorSet="negative"
            onClick={handleRemoveButtonClicked}
            isLoading={status === 'loading'}
          >
            {t('Button.Remove', 'Remove', 'Remove button text')}
          </LoadingButton>
        ) : (
          <ButtonPrimary
            onClick={handleRedirectButtonClicked}
            disabled={isPrePublish}
            colorSet="negative"
          >
            {t('Button.Redirect', 'Redirect', 'Redirect button text')}
          </ButtonPrimary>
        )}
      </RedirectPodcastContainer>
      {shouldShowConfirmationModal && (
        <RedirectConfirmationModal
          hideConfirmationModal={hideConfirmationModal}
          handleSubmit={handleSubmit}
          status={status}
        />
      )}
    </RedirectSection>
  );
}
